import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { Bot, BellRing, Inbox, Lock, ShieldCheck, Users } from 'lucide-react';
import { BrandMark } from '../components/ui/BrandMark';
import { useAuthConfig } from '../lib/firebase';

// Public pages (no sign-in): about, privacy policy and terms. The operator's name, contact email and the date the
// policies took effect come from the server (LEGAL_* settings), so they can change without a new build.

function useLegal() {
  const l = useAuthConfig().data?.legal;
  return { name: l?.entityName ?? 'KGW Apps', email: l?.contactEmail ?? 'support@kgwapps.com', date: l?.effectiveDate ?? '2026-10-11' };
}

function PublicShell({ title, children }: { title?: string; children: ReactNode }) {
  const { name } = useLegal();
  return (
    <div className="min-h-screen min-h-[100dvh] overflow-y-auto bg-bg text-ink">
      <header className="mx-auto flex max-w-3xl items-center justify-between px-5 py-6">
        <Link to="/about" className="flex items-center gap-2.5">
          <BrandMark size={36} />
          <span className="text-lg font-semibold">MailPilot</span>
        </Link>
        <Link to="/login" className="rounded-full bg-primary px-4 py-2 text-[0.8125rem] font-semibold text-primary-fg hover:bg-primary-hover">
          Sign in
        </Link>
      </header>
      <main className="mx-auto max-w-3xl px-5 pb-16">
        {title && <h1 className="mb-6 text-3xl font-semibold tracking-tight">{title}</h1>}
        {children}
      </main>
      <footer className="mx-auto flex max-w-3xl flex-wrap gap-x-5 gap-y-2 border-t border-line px-5 py-6 text-[0.8125rem] text-muted">
        <span>© {new Date().getFullYear()} {name}</span>
        <Link to="/about" className="hover:text-ink">About</Link>
        <Link to="/privacy" className="hover:text-ink">Privacy policy</Link>
        <Link to="/terms" className="hover:text-ink">Terms of service</Link>
      </footer>
    </div>
  );
}

const H2 = ({ children }: { children: ReactNode }) => <h2 className="mb-2 mt-8 text-lg font-semibold">{children}</h2>;
const P = ({ children }: { children: ReactNode }) => <p className="mb-3 text-[0.9375rem] leading-relaxed text-ink/90">{children}</p>;
const UL = ({ children }: { children: ReactNode }) => <ul className="mb-3 list-disc space-y-1.5 pl-5 text-[0.9375rem] leading-relaxed text-ink/90">{children}</ul>;

export function AboutPage() {
  const { name, email } = useLegal();
  const features = [
    { icon: <Inbox className="h-5 w-5" />, title: 'Every inbox in one place', text: 'Connect Gmail, Outlook and any IMAP account. MailPilot sorts mail, shows who is waiting on you and keeps threads together.' },
    { icon: <Bot className="h-5 w-5" />, title: 'AI that you stay in control of', text: 'Draft replies and summaries that you review and edit. Nothing is sent by AI unless you approve it or set up a rule that allows it.' },
    { icon: <Users className="h-5 w-5" />, title: 'Shared team inboxes', text: 'Assign conversations, leave private team notes and see who is replying, with divisions that keep some mailboxes private.' },
    { icon: <BellRing className="h-5 w-5" />, title: 'Reminders that don’t get lost', text: 'Persistent reminders after automatic replies, follow-ups when nobody answers, and a calm notification island on your desktop.' },
    { icon: <ShieldCheck className="h-5 w-5" />, title: 'Private by design', text: 'Credentials are encrypted, AI only runs with your consent, instructions hidden inside emails are ignored, and your mail is never used to train a general AI model.' },
  ];
  return (
    <PublicShell>
      <section className="py-8">
        <h1 className="text-4xl font-semibold leading-tight tracking-tight">Understand every conversation. Reply with confidence.</h1>
        <p className="mt-4 max-w-2xl text-lg text-muted">
          MailPilot is an email assistant for individuals and teams. It connects to your existing mailboxes, helps you see what needs a reply, drafts responses for you to review, and reminds you until things are done.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/signup" className="rounded-full bg-primary px-5 py-2.5 text-[0.9375rem] font-semibold text-primary-fg hover:bg-primary-hover">Create an account</Link>
          <Link to="/login" className="rounded-full border border-line px-5 py-2.5 text-[0.9375rem] font-semibold hover:bg-raised">Sign in</Link>
        </div>
      </section>
      <section className="grid gap-4 sm:grid-cols-2">
        {features.map((f, i) => (
          <div key={f.title} className="rounded-3xl bg-surface p-5 shadow-card">
            <span className={clsx('flex h-11 w-11 items-center justify-center rounded-2xl', ['mp-pill-lime', 'mp-pill-lavender', 'mp-pill-sky', 'mp-pill-yellow', 'mp-pill-mint'][i % 5])}>{f.icon}</span>
            <h3 className="mt-3 font-semibold">{f.title}</h3>
            <p className="mt-1 text-[0.875rem] text-muted">{f.text}</p>
          </div>
        ))}
      </section>
      <section className="mt-10 rounded-2xl bg-surface p-5 shadow-card">
        <h2 className="flex items-center gap-2 font-semibold"><Lock className="h-4 w-4" /> How MailPilot uses Google and Microsoft data</h2>
        <p className="mt-2 text-[0.875rem] text-muted">
          When you connect Gmail or Outlook, MailPilot reads and organises your messages and sends replies you approve. This data is used only to provide these features to you. Read the <Link to="/privacy" className="text-primary-ink underline">privacy policy</Link> for details. MailPilot is operated by {name} · <a className="text-primary-ink underline" href={`mailto:${email}`}>{email}</a>.
        </p>
      </section>
    </PublicShell>
  );
}

export function PrivacyPage() {
  const { name, email, date } = useLegal();
  return (
    <PublicShell title="Privacy policy">
      <P>Effective {date}. This policy explains what information MailPilot (“we”, “us”), operated by {name}, collects, how it is used and the choices you have. Contact: <a className="text-primary-ink underline" href={`mailto:${email}`}>{email}</a>.</P>

      <H2>Information we collect</H2>
      <UL>
        <li><strong>Account information:</strong> your name, email address, password (stored only as a secure hash), workspace memberships and settings. If you sign in with Google or Microsoft, we receive your name, email address and a unique account identifier from them.</li>
        <li><strong>Mailbox data you connect:</strong> when you connect Gmail, Outlook/Microsoft 365 or an IMAP account, we access message content, headers, attachments, folders and labels so we can show, organise and search your mail, and send messages you approve. Access tokens and app passwords are encrypted at rest.</li>
        <li><strong>Content you create:</strong> drafts, rules, signatures, notes, reminders and files you upload.</li>
        <li><strong>Technical information:</strong> device names you register, sign-in times, and error reports needed to keep the service working. We do not use advertising trackers.</li>
      </UL>

      <H2>How we use information</H2>
      <UL>
        <li>To provide MailPilot’s features: syncing and displaying mail, categorising conversations, drafting replies, sending messages and automatic responses you have enabled, reminders and notifications.</li>
        <li>To keep the service secure and reliable, including preventing abuse, diagnosing errors and making backups.</li>
        <li>To communicate with you about your account, such as password resets and, if you turn it on, a daily summary email.</li>
      </UL>
      <P>We do not sell your information, use it for advertising, or use your mailbox content to train general-purpose AI models.</P>

      <H2>Google user data</H2>
      <P>
        MailPilot’s use and transfer of information received from Google APIs to any other app will adhere to the{' '}
        <a className="text-primary-ink underline" href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.
      </P>
      <UL>
        <li>We use Gmail data only to provide and improve the user-facing email features of MailPilot that you can see in the app.</li>
        <li>We do not transfer Gmail data to others except as needed to provide those features (for example to an AI provider you have enabled, with your consent), to comply with law, or as part of a merger or acquisition with notice to you.</li>
        <li>We do not use Gmail data for advertising, and we do not allow humans to read it unless you give explicit permission for a specific message, it is needed for security purposes such as investigating abuse, or to comply with law.</li>
      </UL>

      <H2>AI processing</H2>
      <P>AI features only run after you give consent in Settings. When they run, the relevant email text is sent to the AI provider configured for your workspace — either a provider you add with your own key, or the built-in provider (the Google Gemini API). AI providers process this text to return a result and are not permitted by MailPilot to use it for any other purpose. You can exclude senders and topics, redact patterns such as card numbers, and withdraw consent at any time.</P>

      <H2>Service providers</H2>
      <P>We use these providers to run MailPilot. They process data only on our instructions:</P>
      <UL>
        <li>Railway (application hosting and database)</li>
        <li>Cloudflare (encrypted backups)</li>
        <li>Google Firebase (sign-in) and Google Gemini (built-in AI, when enabled)</li>
        <li>Brevo (system emails such as password resets and daily summaries)</li>
        <li>AI providers you choose to connect yourself</li>
      </UL>

      <H2>Retention and deletion</H2>
      <P>We keep your data while your account is active. Workspace owners can set how long local copies of mail are kept. You can export your data and delete your account in Settings → Data; deleting removes your data from MailPilot (it never deletes mail at your email provider). Encrypted backups are kept for up to 30 days and then permanently removed. You can also revoke MailPilot’s access at any time from your Google or Microsoft account settings.</P>

      <H2>Security</H2>
      <P>Connections are encrypted (HTTPS/TLS). Tokens, passwords for IMAP accounts and AI keys are encrypted at rest. Backups are encrypted before they leave our servers. Two-step sign-in is available for password accounts.</P>

      <H2>Your rights</H2>
      <P>Depending on where you live, you may have the right to access, correct, export or delete your personal data, or to object to certain processing. Most of this is available directly in the app; for anything else, contact <a className="text-primary-ink underline" href={`mailto:${email}`}>{email}</a>.</P>

      <H2>Children</H2>
      <P>MailPilot is not intended for children under 16 and we do not knowingly collect their data.</P>

      <H2>Changes</H2>
      <P>We will post changes to this policy on this page and update the effective date. If changes are significant, we will notify you in the app or by email.</P>
    </PublicShell>
  );
}

export function TermsPage() {
  const { name, email, date } = useLegal();
  return (
    <PublicShell title="Terms of service">
      <P>Effective {date}. These terms are an agreement between you and {name} (“we”, “us”) for your use of MailPilot. By creating an account or using MailPilot you agree to them.</P>

      <H2>The service</H2>
      <P>MailPilot connects to email accounts you authorise and helps you read, organise and reply to email, including with AI features and automatic responses you configure. Features may change over time.</P>

      <H2>Your account</H2>
      <UL>
        <li>You must give accurate information and keep your sign-in details secure. You are responsible for activity under your account.</li>
        <li>You may only connect mailboxes you own or are authorised to manage. Organization owners and admins are responsible for who they invite and what they share.</li>
      </UL>

      <H2>Automatic sending and AI</H2>
      <UL>
        <li>You decide which rules may send email automatically. You are responsible for messages sent from your mailboxes, including automatic replies you enable.</li>
        <li>AI-generated text can be wrong or incomplete. Review drafts before sending; MailPilot shows what each draft is based on to help you check it.</li>
      </UL>

      <H2>Acceptable use</H2>
      <P>Do not use MailPilot to send spam or unlawful content, to access accounts without permission, to break the law or the policies of your email provider, or to interfere with the service or other users.</P>

      <H2>Your content</H2>
      <P>You keep all rights to your email and content. You give us permission to process it only as needed to provide MailPilot to you, as described in the <Link to="/privacy" className="text-primary-ink underline">privacy policy</Link>.</P>

      <H2>Availability and changes</H2>
      <P>We work to keep MailPilot available and your data backed up, but the service is provided “as is” without warranties of any kind, to the extent the law allows. We may change or discontinue features, and will give reasonable notice of significant changes.</P>

      <H2>Liability</H2>
      <P>To the extent the law allows, we are not liable for indirect or consequential losses, or for losses caused by messages you or your rules send, by AI output you choose to use, or by your email provider. Nothing in these terms limits liability that cannot be limited by law.</P>

      <H2>Ending your use</H2>
      <P>You can stop using MailPilot and delete your account at any time in Settings → Data. We may suspend accounts that break these terms, and will tell you why where we can.</P>

      <H2>Changes to these terms</H2>
      <P>We will post updates here and change the effective date. If you keep using MailPilot after changes take effect, you accept the updated terms.</P>

      <H2>Contact</H2>
      <P>Questions about these terms: <a className="text-primary-ink underline" href={`mailto:${email}`}>{email}</a>.</P>
    </PublicShell>
  );
}
